import type { Layout } from './types';

export function checkLayoutSemantics(layout: Layout): readonly string[] {
  const errors: string[] = [];
  const ids = new Set<string>();

  for (const channel of layout.water.channels) {
    if (ids.has(channel.id)) {
      errors.push(`Duplicate channel id: ${channel.id}`);
    }
    ids.add(channel.id);

    if (channel.widths.length !== channel.centerline.length) {
      errors.push(
        `Channel ${channel.id}: widths.length (${channel.widths.length}) !== centerline.length (${channel.centerline.length})`
      );
    }

    if (channel.centerline.length < 2) {
      errors.push(`Channel ${channel.id}: centerline must have at least 2 points`);
    }

    for (const w of channel.widths) {
      if (w <= 0 || !isFinite(w)) {
        errors.push(`Channel ${channel.id}: invalid width ${w}`);
      }
    }

    for (const pt of channel.centerline) {
      if (!isFinite(pt[0]) || !isFinite(pt[1])) {
        errors.push(`Channel ${channel.id}: non-finite coordinate`);
      }
    }
  }

  for (const street of layout.streets) {
    if (ids.has(street.id)) {
      errors.push(`Duplicate street id: ${street.id}`);
    }
    ids.add(street.id);

    if (street.centerline.length < 2) {
      errors.push(`Street ${street.id}: centerline must have at least 2 points`);
    }

    if (street.width <= 0 || !isFinite(street.width)) {
      errors.push(`Street ${street.id}: invalid width ${street.width}`);
    }
  }

  for (const landmark of layout.landmarks) {
    if (ids.has(landmark.id)) {
      errors.push(`Duplicate landmark id: ${landmark.id}`);
    }
    ids.add(landmark.id);

    if (landmark.footprint.length < 3) {
      errors.push(`Landmark ${landmark.id}: footprint must have at least 3 points`);
    }
  }

  for (const wall of layout.fortifications.walls) {
    if (ids.has(wall.id)) {
      errors.push(`Duplicate wall id: ${wall.id}`);
    }
    ids.add(wall.id);

    if (wall.path.length < 2) {
      errors.push(`Wall ${wall.id}: path must have at least 2 points`);
    }
  }

  for (const tower of layout.fortifications.towers) {
    if (ids.has(tower.id)) {
      errors.push(`Duplicate tower id: ${tower.id}`);
    }
    ids.add(tower.id);

    if (tower.radius <= 0 || !isFinite(tower.radius)) {
      errors.push(`Tower ${tower.id}: invalid radius ${tower.radius}`);
    }
  }

  for (const gate of layout.fortifications.gates) {
    if (ids.has(gate.id)) {
      errors.push(`Duplicate gate id: ${gate.id}`);
    }
    ids.add(gate.id);
  }

  for (const garden of layout.gardens) {
    if (ids.has(garden.id)) {
      errors.push(`Duplicate garden id: ${garden.id}`);
    }
    ids.add(garden.id);

    const gardenPoly = garden.polygon as unknown as unknown[] | undefined;
    if (!gardenPoly || gardenPoly.length < 3) {
      errors.push(`Garden ${garden.id}: polygon must have at least 3 points`);
    }
  }

  for (const polder of layout.polders) {
    if (ids.has(polder.id)) {
      errors.push(`Duplicate polder id: ${polder.id}`);
    }
    ids.add(polder.id);

    const polderPoly = polder.polygon as unknown as unknown[] | undefined;
    if (!polderPoly || polderPoly.length < 3) {
      errors.push(`Polder ${polder.id}: polygon must have at least 3 points`);
    }

    if (!isFinite(polder.ditchAngle)) {
      errors.push(`Polder ${polder.id}: invalid ditchAngle`);
    }

    if (polder.ditchSpacing <= 0 || !isFinite(polder.ditchSpacing)) {
      errors.push(`Polder ${polder.id}: invalid ditchSpacing ${polder.ditchSpacing}`);
    }
  }

  return errors;
}
